// src/types/products.ts

// ===== Enums & base types =====
export enum ProductCategory {
  Pendant = "Pendant",
  Engraving = "Engraving",
  Cup = "Cup",
  LicensePlate = "LicensePlate",
  Wallet = "Wallet",
  PhoneCover = "PhoneCover",
  Tshirt = "Tshirt",
  Gift = "Gift",
}

export type Money = number;

export interface Dimensions {
  width?: number;
  height?: number;
  depth?: number;
  diameter?: number;
}

export type PhotoCustomizationShape = "circle" | "rounded-rect" | "heart";
export type PhotoCustomizationCoordinateSpace = "container" | "image";

export interface PhotoCustomizationArea {
  enabled?: boolean;
  shape?: PhotoCustomizationShape;
  coordinateSpace?: PhotoCustomizationCoordinateSpace;
  xPct?: number;
  yPct?: number;
  widthPct?: number;
  heightPct?: number;
  centerXPct?: number;
  centerYPct?: number;
  radiusPct?: number;
  cornerRadiusPct?: number;
  radiusPx?: number;
  heartTopWidthPct?: number;
  heartNotchDepthPct?: number;
  heartNotchWidthPct?: number;
}

export interface PendantChainOption {
  chainType: string;
  lengthsInch: number[];
  colors?: string[];
  image?: string;
  defaultIncluded?: boolean;
  upgradePrice?: number;
  standalonePrice?: number;
}

export interface VirtualTryOnPoint {
  x: number;
  y: number;
}

export interface VirtualTryOnBounds {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface VirtualTryOnPhotoRegion extends VirtualTryOnBounds {
  shape: "circle" | "ellipse" | "rect" | "custom";
  rotation?: number;
}

export interface VirtualTryOnPhysicalMeasurement {
  start: VirtualTryOnPoint;
  end: VirtualTryOnPoint;
  value: number;
  unit: "mm" | "in";
  measurementType?: "width" | "height" | "diameter" | "wingSpan" | "custom";
  assetPixelsPerMm?: number;
}

export interface VirtualTryOnConfig {
  enabled: boolean;
  assetUrl?: string;
  label?: string;
  color?: string;
  side?: "front" | "back" | "default";
  anchors?: {
    leftChain?: VirtualTryOnPoint;
    rightChain?: VirtualTryOnPoint;
    pendantTop?: VirtualTryOnPoint;
    pendantCenter?: VirtualTryOnPoint;
    pendantBottom?: VirtualTryOnPoint;
  };
  imageWidthPx?: number;
  imageHeightPx?: number;
  opaqueBounds?: VirtualTryOnBounds;
  fullAssetBounds?: VirtualTryOnBounds;
  pendantBounds?: VirtualTryOnBounds;
  pendantBodyBounds?: VirtualTryOnBounds;
  photoRegion?: VirtualTryOnPhotoRegion;
  photoCompositionMode?: "overlay" | "replace-region";
  photoClipping?: boolean;
  frameOverlayUrl?: string;
  physicalMeasurement?: VirtualTryOnPhysicalMeasurement;
  assetPixelsPerMm?: number;
  physicalScaleCorrection?: number;
  physicalScaleCalibratedAt?: string;
  physicalScaleCalibrationNote?: string;
  chainIncluded?: boolean;
  defaultScale?: number;
  verticalOffset?: number;
  horizontalOffset?: number;
  rotationOffset?: number;
  defaultVerticalOffset?: number;
  defaultHorizontalOffset?: number;
  defaultRotationOffset?: number;
  pendantWidthMm?: number;
  pendantHeightMm?: number;
  pendantThicknessMm?: number;
  chainLengthMm?: number;
  chainLengthInches?: number;
  wearOffsetXMm?: number;
  wearOffsetYMm?: number;
  wearDropMm?: number;
  wearRotationDeg?: number;
  variants?: VirtualTryOnConfig[];
}

export interface PendantColorStock {
  color: string;
  stockCount: number;
}

export interface PendantColorPrice {
  color: string;
  price: Money;
}

export interface PendantColorVariant {
  color: string;
  frontImage?: string;
  backImage?: string;
  moreImages?: string[];
  photoCustomization?: PhotoCustomizationArea;
  backPhotoCustomization?: PhotoCustomizationArea;
  frontImageFile?: File | null;
  backImageFile?: File | null;
  moreImageFiles?: File[];
}

export type EngravingSide = "front" | "back";
export type EngravingAreaShape = "rectangle";
export type EngravingFontKey =
  | "serif"
  | "sans"
  | "script"
  | "handwritten"
  | "bold-classic"
  | string;
export type EngravingEmojiSupportMode = "controlled" | "unicode";

export interface EngravingArea {
  side: EngravingSide;
  shape?: EngravingAreaShape;
  enabled?: boolean;
  xPct?: number;
  yPct?: number;
  widthPct?: number;
  heightPct?: number;
  rotation?: number;
  maxLines?: number;
  textLimit?: number;
}

export type EngravingColorStock = PendantColorStock;
export type EngravingColorPrice = PendantColorPrice;

export interface EngravingChainByColor {
  color: string;
  chainName?: string;
  chainColor?: string;
  chainSku?: string;
  chainIncluded?: boolean;
  image?: string;
}

export interface EngravingColorVariant {
  color: string;
  sku?: string;
  stockCount?: number;
  price?: Money;
  swatchHex?: string;
  frontImage?: string;
  backImage?: string;
  moreImages?: string[];
  matchingChain?: EngravingChainByColor;
  frontImageFile?: File | null;
  backImageFile?: File | null;
  moreImageFiles?: File[];
}

// ===== Base DTO returned by API (READ shape) =====
export interface ProductBaseDTO {
  _id?: string;
  category: ProductCategory;
  name: string;
  slug: string;
  description?: string;
  heroImage?: string;
  jewelryCareHtml?: string;

  // New image model
  previewImage?: string; // primary image URL
  model3dUrl?: string;
  model3dPhotoMaterial?: string;
  model3dEngravingMaterial?: string;
  model3dWidthMm?: number | null;
  arImage?: string; // AR asset URL (.glb/.usdz) or image
  moreImages?: string[]; // gallery URLs
  youtubeVideoUrl?: string;
  useYoutubeAsThumbnail?: boolean;
  virtualTryOn?: VirtualTryOnConfig;

  // Legacy gallery (kept for compat; BE may auto-sync)
  images: string[];

  thumbnail?: string;
  color?: string;
  material?: string;
  dimensions?: Dimensions;

  price: Money;
  compareAtPrice?: Money;
  offerPercent?: number;

  featured?: boolean;
  topSeller?: boolean;
  tags?: string[];
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string[];
  seoSlug?: string;
  sku?: string;
  averageRating?: number;
  reviewCount?: number;

  // Inventory
  stockCount: number;
  isCustomizable?: boolean;
  giftWrapEnabled?: boolean;

  isActive: boolean;

  createdAt?: string;
  updatedAt?: string;
}

// ===== Category-specific fields (READ/WRITE domain types) =====
export interface PendantFields {
  chainType?: string; // legacy
  chainLengthInch?: number[]; // legacy
  chainOptions?: PendantChainOption[];
  availableColors?: string[];
  colorStocks?: PendantColorStock[];
  colorPrices?: PendantColorPrice[];
  pendantColorVariants?: PendantColorVariant[];
  isDoubleSidedPendant?: boolean;
  photoUploadAllowed?: boolean;
  photoCustomization?: PhotoCustomizationArea;
  backPhotoCustomization?: PhotoCustomizationArea;
  backImage?: string;
  engraving?: {
    enabled: boolean;
    text?: string;
    font?: string;
    maxLength?: number;
    extraCost?: Money;
  };
}

export interface EngravingFields {
  engravingSubtype?: string;
  baseItem?: "Plate" | "Pendant" | "Wallet" | "Other";
  backImage?: string;
  availableColors?: string[];
  colorStocks?: EngravingColorStock[];
  colorPrices?: EngravingColorPrice[];
  engravingColorVariants?: EngravingColorVariant[];
  defaultChainByColor?: EngravingChainByColor[];
  engravingAreas?: EngravingArea[];
  frontEngravingArea?: EngravingArea;
  backEngravingArea?: EngravingArea;
  supportsBackEngraving?: boolean;
  backEngravingExtraCost?: Money;
  frontTextLimit?: number;
  backTextLimit?: number;
  frontMaxLines?: number;
  backMaxLines?: number;
  allowedFonts?: EngravingFontKey[];
  defaultFont?: EngravingFontKey;
  defaultFontSize?: number;
  allowedSymbols?: string[];
  emojiSupportMode?: EngravingEmojiSupportMode;
}

export interface CupFields {
  size?: string;
  photoUploadAllowed?: boolean;
}

export interface LicensePlateFields {
  plateType?: "standard_car_12x6" | "motorcycle";
}

export interface WalletFields {
  slots?: number;
  hasCoinPocket?: boolean;
  photoEmbedAllowed?: boolean;
}

export interface PhoneCaseCameraCutout {
  xPct?: number;
  yPct?: number;
  widthPct?: number;
  heightPct?: number;
  cornerRadiusPct?: number;
}

export interface PhoneCoverFields {
  phoneModel?: string;
  caseSize?: string;
  cameraCutout?: PhoneCaseCameraCutout;
  finish?: "Matte" | "Glossy" | "SoftTouch";
  photoUploadAllowed?: boolean;
}

export interface TshirtColorStock {
  color: string;
  stockCount: number;
}

export interface TshirtSizeStock {
  size: "XS" | "S" | "M" | "L" | "XL" | "XXL" | string;
  stockCount: number;
}

export interface TshirtColorSizeStock {
  color: string;
  sizes: TshirtSizeStock[];
}

export interface TshirtColorVariant {
  color: string;
  frontImage?: string;
  backImage?: string;
  moreImages?: string[];
  // admin write-only file handles
  frontImageFile?: File | null;
  backImageFile?: File | null;
  moreImageFiles?: File[];
}

export interface TshirtFields {
  // Deprecated: single-size field
  size?: "XS" | "S" | "M" | "L" | "XL" | "XXL";
  tshirtType?: "Round" | "V";
  backCustomizationExtraCost?: Money;
  photoUploadAllowed?: boolean;
  availableColors?: string[];
  colorStocks?: TshirtColorStock[];
  availableSizes?: Array<"XS" | "S" | "M" | "L" | "XL" | "XXL" | string>;
  sizeStocks?: TshirtSizeStock[];
  sizeStockByColor?: TshirtColorSizeStock[];
  colorVariants?: TshirtColorVariant[];
}

export interface GiftFields {
  giftType?: string;
}

// ===== Discriminated Product union (READ shape) =====
export type Product =
  | (ProductBaseDTO & { category: ProductCategory.Pendant } & PendantFields)
  | (ProductBaseDTO & { category: ProductCategory.Engraving } & EngravingFields)
  | (ProductBaseDTO & { category: ProductCategory.Cup } & CupFields)
  | (ProductBaseDTO & {
      category: ProductCategory.LicensePlate;
    } & LicensePlateFields)
  | (ProductBaseDTO & { category: ProductCategory.Wallet } & WalletFields)
  | (ProductBaseDTO & {
      category: ProductCategory.PhoneCover;
    } & PhoneCoverFields)
  | (ProductBaseDTO & { category: ProductCategory.Tshirt } & TshirtFields)
  | (ProductBaseDTO & { category: ProductCategory.Gift } & GiftFields);

// List response (GET /products)
export interface ProductListResponse {
  items: Product[];
  page: number;
  limit: number;
  total: number;
}

// ===== Admin upload extras (WRITE-only, FE → BE) =====
export type UploadExtras = {
  heroImageFile?: File;
  previewImageFile?: File;
  model3dFile?: File;
  clearModel3d?: boolean;
  arImageFile?: File;
  virtualTryOnAssetFile?: File;
  backImageFile?: File;
  moreImageFiles: File[];

  clearHeroImage?: boolean;
  clearPreviewImage?: boolean;
  clearArImage?: boolean;
  clearVirtualTryOnAsset?: boolean;
  clearBackImage?: boolean;
  removeMoreImages?: string[]; // send stored URLs to remove
};

// ===== WRITE DTOs (what FE is allowed to send) =====
export type ProductBaseWriteDTO = {
  category: ProductCategory;
  name: string;
  slug: string;
  description?: string;
  heroImage?: string;
  jewelryCareHtml?: string;

  // We may send existing URLs back; BE overwrites after file upload if needed
  previewImage?: string;
  model3dUrl?: string;
  model3dPhotoMaterial?: string;
  model3dEngravingMaterial?: string;
  model3dWidthMm?: number | null;
  arImage?: string;
  moreImages?: string[];
  youtubeVideoUrl?: string;
  useYoutubeAsThumbnail?: boolean;
  virtualTryOn?: VirtualTryOnConfig;

  // Legacy gallery (optional)
  images?: string[];
  thumbnail?: string;

  color?: string;
  material?: string;
  dimensions?: Dimensions;

  price: Money;
  compareAtPrice?: Money;
  offerPercent?: number;

  featured?: boolean;
  topSeller?: boolean;
  tags?: string[];
  seoTitle?: string;
  seoDescription?: string;
  seoKeywords?: string[];
  seoSlug?: string;
  sku?: string;

  stockCount: number;
  isActive: boolean;
  giftWrapEnabled?: boolean;
};

// Per-category WRITE variants (category is narrowed)
export type PendantWrite = ProductBaseWriteDTO & {
  category: ProductCategory.Pendant;
} & Partial<PendantFields>;

export type EngravingWrite = ProductBaseWriteDTO & {
  category: ProductCategory.Engraving;
} & Partial<EngravingFields>;

export type CupWrite = ProductBaseWriteDTO & {
  category: ProductCategory.Cup;
} & Partial<CupFields>;

export type LicensePlateWrite = ProductBaseWriteDTO & {
  category: ProductCategory.LicensePlate;
} & Partial<LicensePlateFields>;

export type WalletWrite = ProductBaseWriteDTO & {
  category: ProductCategory.Wallet;
} & Partial<WalletFields>;

export type PhoneCoverWrite = ProductBaseWriteDTO & {
  category: ProductCategory.PhoneCover;
} & Partial<PhoneCoverFields>;

export type TshirtWrite = ProductBaseWriteDTO & {
  category: ProductCategory.Tshirt;
} & Partial<TshirtFields>;

export type GiftWrite = ProductBaseWriteDTO & {
  category: ProductCategory.Gift;
} & Partial<GiftFields>;

// ✅ Final WRITE payload (FE → BE)
export type ProductCreateOrUpdatePayload =
  | (PendantWrite & UploadExtras)
  | (EngravingWrite & UploadExtras)
  | (CupWrite & UploadExtras)
  | (LicensePlateWrite & UploadExtras)
  | (WalletWrite & UploadExtras)
  | (PhoneCoverWrite & UploadExtras)
  | (TshirtWrite & UploadExtras)
  | (GiftWrite & UploadExtras);

// ===== Optional: type guards (handy in forms) =====
export type PendantProduct = Extract<
  Product,
  { category: ProductCategory.Pendant }
>;
export type EngravingProduct = Extract<
  Product,
  { category: ProductCategory.Engraving }
>;
export type CupProduct = Extract<Product, { category: ProductCategory.Cup }>;
export type LicensePlateProduct = Extract<
  Product,
  { category: ProductCategory.LicensePlate }
>;
export type WalletProduct = Extract<
  Product,
  { category: ProductCategory.Wallet }
>;
export type PhoneCoverProduct = Extract<
  Product,
  { category: ProductCategory.PhoneCover }
>;
export type TshirtProduct = Extract<
  Product,
  { category: ProductCategory.Tshirt }
>;
export type GiftProduct = Extract<Product, { category: ProductCategory.Gift }>;

export function isPendant(p?: Partial<Product>): p is PendantProduct {
  return p?.category === ProductCategory.Pendant;
}
export function isEngraving(p?: Partial<Product>): p is EngravingProduct {
  return p?.category === ProductCategory.Engraving;
}
export function isCup(p?: Partial<Product>): p is CupProduct {
  return p?.category === ProductCategory.Cup;
}
export function isLicensePlate(p?: Partial<Product>): p is LicensePlateProduct {
  return p?.category === ProductCategory.LicensePlate;
}
export function isWallet(p?: Partial<Product>): p is WalletProduct {
  return p?.category === ProductCategory.Wallet;
}
export function isPhoneCover(p?: Partial<Product>): p is PhoneCoverProduct {
  return p?.category === ProductCategory.PhoneCover;
}
export function isTshirt(p?: Partial<Product>): p is TshirtProduct {
  return p?.category === ProductCategory.Tshirt;
}
export function isGift(p?: Partial<Product>): p is GiftProduct {
  return p?.category === ProductCategory.Gift;
}
